import * as React from 'react';
import { CONFORMA_TASK, EC_TASK } from '~/consts/security';
import { useIsOnFeatureFlag } from '~/feature-flags/hooks';
import { usePipelineRunV2 } from '~/hooks/usePipelineRunsV2';
import { logger } from '~/monitoring/logger';
import { TaskRunKind } from '~/types';
import { ComponentConformaResult, ConformaResult, ConformaResultRow } from '~/types/conforma';
import { isResourceEnterpriseContract } from '~/utils/conforma-utils';
import { isTaskRunInPipelineRun } from '~/utils/pipeline-utils';
import { useTaskRunsForPipelineRuns } from '../../hooks/useTaskRunsV2';
import { useNamespace } from '../../shared/providers/Namespace';
import {
  mapConformaResultData,
  resolveConformaResultFromTaskRun,
} from './ConformaResultsTab/conforma-fetchers';

export { mapConformaResultData };

export const useConformaResultFromLogs = (
  pipelineRunName: string,
  selectedTaskRun?: TaskRunKind,
): [ComponentConformaResult[], boolean, unknown] => {
  const namespace = useNamespace();
  const isKubearchiveEnabled = useIsOnFeatureFlag('kubearchive-logs');
  const [pipelineRun, pipelineRunLoaded, pipelineRunError] = usePipelineRunV2(
    selectedTaskRun ? undefined : namespace,
    pipelineRunName,
  );
  const securityTaskRunName = React.useMemo(() => {
    if (!pipelineRunLoaded || pipelineRunError) {
      return undefined;
    }

    if (isResourceEnterpriseContract(pipelineRun)) {
      return EC_TASK;
    }

    if (isTaskRunInPipelineRun(pipelineRun, CONFORMA_TASK)) {
      return CONFORMA_TASK;
    }

    return undefined;
  }, [pipelineRun, pipelineRunLoaded, pipelineRunError]);
  const [taskRuns, taskRunLoaded, taskRunError] = useTaskRunsForPipelineRuns(
    !selectedTaskRun && securityTaskRunName ? namespace : undefined,
    pipelineRunName,
    securityTaskRunName,
  );
  const [crJson, setCrJson] = React.useState<ConformaResult | undefined>();
  const [crLoaded, setCrLoaded] = React.useState<boolean>(false);
  const taskRun = selectedTaskRun ?? taskRuns?.[0];
  const hasSelectedTask = !!selectedTaskRun;
  const loaded = hasSelectedTask || taskRunLoaded;

  // Keep a ref to the latest taskRun so the effect body always reads the
  // current value without adding the (potentially unstable) object reference
  // to the dep array. A stable primitive derived from the task run is used as
  // the dep instead: prefer uid when available, fall back to podName so that
  // task runs without metadata (e.g. some test/edge-case runs) are still served.
  const taskRunRef = React.useRef(taskRun);
  taskRunRef.current = taskRun;
  const taskRunId = taskRun?.metadata?.uid ?? taskRun?.status?.podName ?? taskRun?.metadata?.name;
  const taskRunVersion = taskRun?.metadata?.resourceVersion;

  React.useEffect(() => {
    setCrJson(undefined);
    setCrLoaded(false);

    if (!loaded || (!hasSelectedTask && !securityTaskRunName) || !taskRunId) return;

    const currentTaskRun = taskRunRef.current;
    if (!currentTaskRun) return;

    let cancelled = false;
    resolveConformaResultFromTaskRun(namespace, currentTaskRun, isKubearchiveEnabled)
      .then((result) => {
        if (!cancelled) {
          setCrJson(result);
          setCrLoaded(true);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          logger.warn('Failed to resolve Conforma result from TaskRun', {
            error: err instanceof Error ? err.message : String(err),
          });
          setCrLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    loaded,
    hasSelectedTask,
    taskRunId,
    taskRunVersion,
    securityTaskRunName,
    namespace,
    isKubearchiveEnabled,
  ]);

  const conformaResult = React.useMemo(() => {
    // filter out components for which Conforma didn't execute because invalid image URL
    return crLoaded && crJson
      ? crJson.components?.filter((comp: ComponentConformaResult) => {
          return !(
            comp.violations &&
            comp.violations?.length === 1 &&
            !comp.violations[0].metadata &&
            comp.violations[0].msg.includes('404 Not Found')
          );
        })
      : undefined;
  }, [crJson, crLoaded]);

  const error = hasSelectedTask ? undefined : (pipelineRunError ?? taskRunError);

  return [conformaResult, error ? true : crLoaded, error];
};

export const useConformaResult = (
  pipelineRunName: string,
  selectedTaskRun?: TaskRunKind,
): [ConformaResultRow[] | undefined, boolean, unknown] => {
  const [cr, crLoaded, crError] = useConformaResultFromLogs(pipelineRunName, selectedTaskRun);
  const conformaResult = React.useMemo(() => {
    return crLoaded && cr && !crError ? mapConformaResultData(cr) : undefined;
  }, [cr, crLoaded, crError]);

  return [conformaResult, crLoaded, crError];
};
